import { z } from 'zod';
import { api } from '@/lib/api';
import type { TutorImage } from './tutorImages';
import type { WorkingTutorRequest } from './workingTutorApi';
const text = z.string().min(1).max(1600);
export const tutorReplySchema = z
  .object({
    format_version: z.literal(1),
    blocks: z
      .array(
        z.discriminatedUnion('type', [
          z.object({ type: z.literal('text'), text }).strict(),
          z
            .object({
              type: z.literal('math'),
              latex: z.string().min(1).max(2000),
              display: z.boolean(),
            })
            .strict(),
          z
            .object({
              type: z.literal('table'),
              headers: z.array(text).min(1).max(6),
              rows: z.array(z.array(text).min(1).max(6)).min(1).max(12),
            })
            .strict(),
        ]),
      )
      .min(1)
      .max(16),
    suggested_questions: z.array(z.string().min(1).max(200)).max(3),
  })
  .strict()
  .refine(
    (v) =>
      v.blocks.every(
        (b) => b.type !== 'table' || b.rows.every((row) => row.length === b.headers.length),
      ),
    'Table columns must match',
  );
export type TutorReply = z.infer<typeof tutorReplySchema>;
export interface TutorChatRequest extends WorkingTutorRequest {
  message: string;
  previous_turns: string[];
  attachments?: TutorImage[];
  pii_warn_acknowledged?: boolean;
  tool_context?: { subject_key?: 'mathematics' | 'chemistry' | 'physics'; tool_id: string; year: number; snapshot: string };
}
export async function sendTutorMessage(id: string, request: TutorChatRequest) {
  const result = z
    .object({
      canvas_id: z.string().uuid(),
      version: z.number().int().positive(),
      turn_id: z.string().uuid(),
      reply: tutorReplySchema,
      stars_charged: z.number().int().nonnegative(),
    })
    .parse(
      await api<unknown>(`/study/working-canvases/${encodeURIComponent(id)}/tutor-chat`, {
        method: 'POST',
        body: request,
      }),
    );
  if (
    result.canvas_id !== id ||
    result.version !== request.expected_version ||
    result.turn_id !== request.idempotency_key
  )
    throw new Error(
      'Tutor returned a different message or working version. Retry your same message.',
    );
  return result;
}

/** Tool chat uses the same renderer and reply contract, with a distinct server-owned receipt scope. */
export async function sendMathToolMessage(sessionId: string, request: TutorChatRequest) {
  return sendToolMessage(sessionId, request);
}
export async function sendToolMessage(sessionId: string, request: TutorChatRequest) {
  const context = request.tool_context;
  if (!context) throw new Error('Tool context is unavailable. Prepare your message again.');
  const result = z
    .object({
      tool_id: z.string(),
      session_id: z.string().uuid(),
      context_version: z.number().int().positive(),
      turn_id: z.string().uuid(),
      reply: tutorReplySchema,
      stars_charged: z.number().int().nonnegative(),
    })
    .parse(
      await api<unknown>(
        `/study/tools/${context.subject_key ?? 'mathematics'}/${encodeURIComponent(context.tool_id)}/tutor-chat`,
        {
          method: 'POST',
          body: {
            session_id: sessionId,
            context_version: request.expected_version,
            year: context.year,
            snapshot: context.snapshot,
            message: request.message,
            previous_turns: request.previous_turns,
            attachments: request.attachments,
            expected_stars: request.expected_stars,
            idempotency_key: request.idempotency_key,
            pii_warn_acknowledged: request.pii_warn_acknowledged,
          },
        },
      ),
    );
  if (
    result.tool_id !== context.tool_id ||
    result.session_id !== sessionId ||
    result.context_version !== request.expected_version ||
    result.turn_id !== request.idempotency_key
  )
    throw new Error('Tutor returned a different tool context. Retry your same message.');
  return { ...result, canvas_id: result.session_id, version: result.context_version };
}
